import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import MarketingTags from '@/components/MarketingTags';
import { ArrowLeft, Search, CreditCard, Plane, CheckCircle, Shield, Clock, DollarSign, Users, Sparkles, Award, Gift, Zap, Star, X, Phone, Mail, ChevronRight, AlertCircle, Info, Calendar, Armchair, MessageSquareText } from 'lucide-react';
import { buildTommyUrl } from '@/constants/support';
import { Button } from '@/components/ui/button';
import Contact from './Contact';
import AboutUs from './AboutUs';
import { PLAN_MIN_DAYS } from '@/content/help/tokens';
import { HOW_IT_WORKS_FAQS } from '@/content/faqs';
import PageSeo from '@/components/PageSeo';

// Defera "sturdy card" pattern — matches the language used in FlightDetailsModal,
// ReserveTripsCard, and the marketing surfaces in IndexMinimal.
function Panel({ children, className = '', innerClassName = 'p-6 sm:p-7 md:p-8', accent }) {
  // Optional accent for theme-tinted cards. Defaults to neutral indigo-white.
  const accentRgb = accent || '127, 163, 247';
  return (
    <div
      className={`group/panel relative overflow-hidden rounded-2xl border border-white/10 backdrop-blur-sm transition-all duration-500 hover:border-white/[0.18] ${className}`}
      style={{
        background:
          'linear-gradient(160deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.025) 35%, rgba(255,255,255,0.015) 100%)',
        boxShadow:
          'inset 0 1px 0 0 rgba(255,255,255,0.06), inset 0 -1px 0 0 rgba(0,0,0,0.4), 0 1px 0 0 rgba(255,255,255,0.02)',
      }}
    >
      {/* Top edge highlight */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none" />
      {/* Corner glow that brightens on hover */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -right-24 w-[360px] h-[360px] opacity-0 group-hover/panel:opacity-100 transition-opacity duration-700"
        style={{
          background: `radial-gradient(circle at center, rgba(${accentRgb},0.13), rgba(${accentRgb},0) 65%)`,
        }}
      />
      <div className={`relative ${innerClassName}`}>{children}</div>
    </div>
  );
}

export default function HowItWorks() {
  const navigate = useNavigate();
  const [overlayContent, setOverlayContent] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Prevent body scroll when overlay is open
  useEffect(() => {
    if (overlayContent) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [overlayContent]);

  const openOverlay = (content) => {
    // The Help Center (/help) and the Security pages (/security) are their
    // own public hubs, not overlays.
    if (content === 'help' || content === 'security' || content === 'privacy-choices') {
      navigate(`/${content}`);
      return;
    }
    setOverlayContent(content);
  };

  const closeOverlay = () => {
    setOverlayContent(null);
  };

  const steps = [
    {
      number: '01',
      icon: Search,
      title: 'Search & Select',
      description: 'Search for flights to any destination worldwide. Our platform connects to major airlines to show you real-time availability and competitive pricing. No hidden fees, no surprise charges—just transparent pricing from the start.'
    },
    {
      number: '02',
      icon: CreditCard,
      title: 'Choose Payment Plan',
      description: 'Select how you want to pay. Defera Zero (refundable fares) lets you book with $0 down—your trip price is protected from the moment you\'re approved, with the first payment due on your scheduled installment date (cancel up to 24 hours before departure for 75% refund). For Defera Installments, your deposit secures your agreed-upon trip price and protects you from future airfare increases while you remain in good standing. Spread the remaining balance over flexible installments. No credit check required. No interest charges. Just simple, manageable payments that fit your budget.'
    },
    {
      number: '03',
      icon: CheckCircle,
      title: 'Complete Payments',
      description: 'Make your scheduled installment payments automatically. Once all payments are completed, your airline confirmation number and e-ticket are dispersed to you. You\'ll receive all confirmation details via email and can manage your trip through your Defera account.'
    },
    {
      number: '04',
      icon: Plane,
      title: 'Travel',
      description: 'Enjoy your trip knowing you paid on your terms. Your agreed-upon trip price was secured when you confirmed your booking, so price protection has been in place since day one. The price you locked in is the price you pay—guaranteed.'
    }
  ];

  const features = [
    {
      icon: Shield,
      title: 'No Credit Check',
      description: 'We believe travel should be accessible to everyone. That\'s why we don\'t require a credit check. Everyone is approved.'
    },
    {
      icon: DollarSign,
      title: 'No Interest',
      description: 'Unlike other "buy now, pay later" services, we charge zero interest. What you see is what you pay—no hidden fees or compound charges.'
    },
    {
      icon: Clock,
      title: 'Flexible Payments',
      description: 'Choose a payment plan that works for you. Pay over weeks or months, with automatic payments that fit your schedule.'
    },
    {
      icon: Users,
      title: 'Price Protection',
      description: 'Our price lock guarantee protects you from price volatility. If fares drop, we pass the savings to you. If they rise, you\'re protected.'
    }
  ];

  const benefits = [
    {
      title: 'Accessible to Everyone',
      description: 'No credit checks, no hidden fees, no predatory interest rates. We built Defera for people who\'ve been excluded by traditional travel booking systems.'
    },
    {
      title: 'Transparent Pricing',
      description: 'See exactly what you\'ll pay upfront. No surprise charges, no last-minute fees. The price we quote is the price you pay.'
    },
    {
      title: 'Real-Time Inventory',
      description: 'We connect directly to major airlines, so you see the same flights and prices as booking directly—but with the flexibility to pay over time.'
    },
    {
      title: '24/7 Support',
      description: 'Our team is here to help whenever you need it. From booking questions to payment assistance, we\'ve got you covered.'
    }
  ];

  return (
    <>
      <PageSeo path="/how-it-works" />
      <MarketingTags contentsquare />
      
      <div className="min-h-screen bg-black text-white">
        {/* Navigation Bar */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/10"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={() => window.location.href = 'https://defera.app'}
              className="text-white/30 hover:text-white/60 text-sm font-normal transition-colors"
            >
              Home
            </button>
            <button
              onClick={() => navigate('/home')}
              className="text-white/30 hover:text-white/60 text-sm font-normal transition-colors"
            >
              Sign Up
            </button>
          </div>
        </div>
      </motion.div>

      {/* ── Hero header ── */}
      <section className="relative pt-24 sm:pt-28 lg:pt-32 pb-12 sm:pb-16 px-5 sm:px-6 border-b border-white/[0.06] overflow-hidden">
        {/* Layered hero glows */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[1100px] h-[620px]"
          style={{ background: 'radial-gradient(ellipse at center, rgba(16, 37, 178,0.32), rgba(16, 37, 178,0) 60%)' }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-20 left-[15%] w-[420px] h-[420px] opacity-70"
          style={{ background: 'radial-gradient(circle at center, rgba(167,243,208,0.10), rgba(167,243,208,0) 60%)' }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-10 right-[10%] w-[380px] h-[380px] opacity-70"
          style={{ background: 'radial-gradient(circle at center, rgba(16, 37, 178,0.08), rgba(16, 37, 178,0) 60%)' }}
        />
        {/* Subtle grid pattern */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
            maskImage: 'radial-gradient(ellipse 60% 50% at 50% 0%, black, transparent)',
            WebkitMaskImage: 'radial-gradient(ellipse 60% 50% at 50% 0%, black, transparent)',
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="relative max-w-6xl mx-auto"
        >
          {/* Eyebrow pill */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5 sm:mb-6"
            style={{ background: 'rgba(10, 18, 51,0.10)', border: '1px solid rgba(10, 18, 51,0.25)' }}>
            <Sparkles className="w-3 h-3" style={{ color: '#7A8ABF' }} />
            <span className="text-[10px] sm:text-[11px] font-medium uppercase tracking-[0.18em]" style={{ color: '#AAB4D6' }}>
              Documentation · How It Works
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-[80px] font-light text-white tracking-[-0.03em] leading-[1.02] mb-5 sm:mb-6">
            How{' '}
            <span className="font-normal" style={{
              background: 'linear-gradient(135deg, #AAB4D6 0%, #7A8ABF 50%, #2A3868 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>Defera</span>{' '}
            works
          </h1>
          <p className="text-[17px] sm:text-[18px] md:text-[19px] text-white/70 leading-relaxed max-w-2xl font-light">
            Everything you need to know before you book — in plain English. Skim the highlights, read what matters to you, and skip the rest.
          </p>
          <div className="flex flex-wrap items-center gap-x-5 sm:gap-x-6 gap-y-2 mt-7 sm:mt-9 text-[11px] sm:text-xs text-white/45">
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-3 h-3" />
              <span>~5 min read</span>
            </span>
            <span className="w-px h-3 bg-white/15" />
            <span>Updated this month</span>
            <span className="w-px h-3 bg-white/15" />
            <a href="#faq" className="inline-flex items-center gap-1 transition-colors hover:opacity-80" style={{ color: '#7A8ABF' }}>
              Jump to FAQ
              <ChevronRight className="w-3 h-3" />
            </a>
          </div>
        </motion.div>
      </section>

      {/* ── TL;DR highlights bar ── */}
      <section className="px-5 sm:px-6 -mt-px">
        <div className="max-w-6xl mx-auto pt-10 sm:pt-12">
          <Panel innerClassName="p-0">
            <div className="grid grid-cols-2 md:grid-cols-4">
              {[
                { num: '0%', label: 'Interest', sub: 'Ever. On anything.', color: '#7A8ABF', glow: 'rgba(122,138,191,0.18)', icon: DollarSign },
                { num: '0', label: 'Credit checks', sub: 'Everyone\u2019s approved.', color: '#5eead4', glow: 'rgba(94,234,212,0.16)', icon: Shield },
                { num: '100%', label: 'Price locked', sub: 'From the second you book.', color: '#7A8ABF', glow: 'rgba(122,138,191,0.16)', icon: CheckCircle },
                { num: '$0', label: 'Hidden fees', sub: 'No tricks, no surprises.', color: '#D5DAEB', glow: 'rgba(216,228,253,0.14)', icon: Sparkles },
              ].map(({ num, label, sub, color, glow, icon: Icon }, i) => (
                <div key={label}
                  className={`group relative overflow-hidden p-6 sm:p-7 md:p-8 transition-colors duration-300 hover:bg-white/[0.02] ${i < 3 ? 'border-r border-white/[0.06]' : ''} ${i < 2 ? 'border-b md:border-b-0 border-white/[0.06]' : ''} ${i === 2 ? 'md:border-r border-white/[0.06]' : ''} ${i === 1 ? 'border-r-0 md:border-r border-white/[0.06]' : ''}`}>
                  {/* Hover glow */}
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -bottom-12 left-1/2 -translate-x-1/2 w-[220px] h-[140px] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ background: `radial-gradient(ellipse at center, ${glow}, transparent 70%)` }}
                  />
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-3 sm:mb-4">
                      <Icon className="w-3.5 h-3.5" style={{ color }} />
                      <div className="text-[10px] sm:text-[11px] font-medium uppercase tracking-[0.18em]" style={{ color: `${color}cc` }}>
                        {label}
                      </div>
                    </div>
                    <div className="text-4xl sm:text-5xl md:text-6xl font-extralight mb-2 tracking-[-0.03em] tabular-nums" style={{ color }}>
                      {num}
                    </div>
                    <div className="text-xs sm:text-[13px] text-white/55 leading-relaxed">{sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </section>

      {/* ── Two-column doc layout ── */}
      <div className="max-w-6xl mx-auto px-5 sm:px-6 py-10 sm:py-14 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] lg:gap-14 xl:gap-16">

          {/* Sticky sidebar TOC — desktop only */}
          <aside className="hidden lg:block lg:sticky lg:top-24 lg:self-start lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
            <div className="text-[10px] uppercase tracking-[0.2em] font-normal text-white/35 mb-4">On this page</div>
            <nav className="text-sm space-y-1">
              {[
                { id: 'overview', label: '1. Overview' },
                { id: 'process', label: '2. Booking process' },
                { id: 'plans', label: '3. Payment plans' },
                { id: 'schedules', label: '4. Example schedules' },
                { id: 'eligibility', label: '5. Eligibility' },
                { id: 'scenarios', label: '6. What if...' },
                { id: 'rewards', label: '7. Miles & rewards' },
                { id: 'extras', label: '8. Credits & JSX' },
                { id: 'faq', label: '9. FAQ' },
              ].map((item) => (
                <a key={item.id} href={`#${item.id}`}
                  className="block py-1.5 text-white/45 hover:text-white border-l border-transparent hover:border-white/30 pl-3 -ml-3 transition-colors">
                  {item.label}
                </a>
              ))}
            </nav>
            <div className="mt-8 pt-6 border-t border-white/[0.06]">
              <div className="text-[10px] uppercase tracking-[0.2em] font-normal text-white/35 mb-3">Need help?</div>
              <Link to="/contact#message" className="text-xs text-white/55 hover:text-white block mb-1">Send us a message</Link>
              <button onClick={() => openOverlay('help')} className="text-xs hover:underline" style={{ color: '#7A8ABF' }}>Open help center</button>
            </div>
          </aside>

          {/* Mobile-only inline TOC (collapsible) */}
          <details className="lg:hidden mb-8 group">
            <summary className="cursor-pointer list-none flex items-center justify-between gap-3 px-5 py-3.5 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm shadow-[0_0_0_1px_rgba(255,255,255,0.03)_inset]">
              <span className="text-[11px] uppercase tracking-[0.2em] font-normal text-white/55">On this page</span>
              <ChevronRight className="w-4 h-4 text-white/35 transition-transform group-open:rotate-90" />
            </summary>
            <nav className="mt-2 px-2 py-2 rounded-2xl border border-white/[0.06] bg-white/[0.02]">
              {[
                { id: 'overview', label: '1. Overview' },
                { id: 'process', label: '2. Booking process' },
                { id: 'plans', label: '3. Payment plans' },
                { id: 'schedules', label: '4. Example schedules' },
                { id: 'eligibility', label: '5. Eligibility' },
                { id: 'scenarios', label: '6. What if...' },
                { id: 'rewards', label: '7. Miles & rewards' },
                { id: 'extras', label: '8. Credits & JSX' },
                { id: 'faq', label: '9. FAQ' },
              ].map((item) => (
                <a key={item.id} href={`#${item.id}`} className="block px-3 py-2 text-sm text-white/55 hover:text-white rounded-lg hover:bg-white/[0.04] transition-colors">{item.label}</a>
              ))}
            </nav>
          </details>

          {/* Main content column */}
          <main className="min-w-0">

            {/* 1. Overview */}
            <section id="overview" className="scroll-mt-28">
              <div className="flex items-center gap-3 mb-4 sm:mb-5">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full text-[14px] font-medium tabular-nums flex-shrink-0 shadow-[inset_0_0_0_1px_rgba(122,138,191,0.32),0_0_28px_-6px_rgba(122,138,191,0.55)]" style={{ background: 'linear-gradient(135deg, rgba(122,138,191,0.22) 0%, rgba(10, 18, 51,0.08) 100%)', color: '#AAB4D6' }}>1</span>
                <h2 className="text-2xl sm:text-3xl font-light text-white tracking-[-0.015em]">Overview</h2>
              </div>
              <p className="text-[16px] sm:text-[17px] text-white/80 leading-relaxed mb-7 max-w-2xl">
                <span className="text-white">Defera is flight layaway.</span> Book today at today's price, pay it off over time, get your e-ticket once you're done. No credit check, no interest, no catch.
              </p>

              <Panel innerClassName="p-0">
                <table className="w-full text-sm">
                  <tbody>
                    {[
                      { k: 'What it is', v: 'Flight layaway — pay over time, fly when paid off', icon: Plane },
                      { k: 'Where it works', v: 'Any airline, any route', icon: Search },
                      { k: 'Credit check', v: 'Never. Everyone gets approved.', icon: Shield },
                      { k: 'Interest', v: '0%. Never any. Promise.', icon: DollarSign },
                      { k: 'Fees', v: 'Just a small service fee. No interest. No credit check.', icon: Info },
                      { k: 'Deposit', v: 'Small one for Installments · $0 for Defera Zero', icon: CreditCard },
                      { k: 'Currency', v: 'USD', icon: DollarSign },
                      { k: 'Plans', v: 'Defera Installments  ·  Defera Zero', icon: Sparkles },
                    ].map(({ k, v, icon: Icon }, i, arr) => (
                      <tr key={k} className="group/row transition-colors hover:bg-white/[0.025]">
                        <td className={`py-4 px-5 sm:px-6 align-top text-[13px] sm:text-[14px] w-[42%] sm:w-[35%] ${i < arr.length - 1 ? 'border-b border-white/[0.04]' : ''}`}>
                          <div className="flex items-center gap-2.5">
                            <Icon className="w-3.5 h-3.5 text-white/30 flex-shrink-0 transition-colors group-hover/row:text-[#7A8ABF]" />
                            <span className="text-white/55 font-normal uppercase tracking-[0.06em] text-[11px] sm:text-[12px]">{k}</span>
                          </div>
                        </td>
                        <td className={`py-4 px-5 sm:px-6 text-white/90 font-normal text-[14px] sm:text-[15px] ${i < arr.length - 1 ? 'border-b border-white/[0.04]' : ''}`}>{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Panel>
            </section>

            {/* 2. Booking process */}
            <section id="process" className="scroll-mt-28 mt-16 sm:mt-20 lg:mt-24 pt-12 sm:pt-14 relative before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-white/15 before:to-transparent">
              <div className="flex items-center gap-3 mb-4 sm:mb-5">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full text-[14px] font-medium tabular-nums flex-shrink-0 shadow-[inset_0_0_0_1px_rgba(122,138,191,0.32),0_0_28px_-6px_rgba(122,138,191,0.55)]" style={{ background: 'linear-gradient(135deg, rgba(122,138,191,0.22) 0%, rgba(10, 18, 51,0.08) 100%)', color: '#AAB4D6' }}>2</span>
                <h2 className="text-2xl sm:text-3xl font-light text-white tracking-[-0.015em]">Booking process</h2>
              </div>
              <p className="text-[16px] sm:text-[17px] text-white/80 leading-relaxed mb-7 sm:mb-9 max-w-2xl">
                Four stages, all together under 5 minutes (not counting the time you spend deciding where to go).
              </p>

              <div className="space-y-3 sm:space-y-4">
                {[
                  {
                    n: '01', title: 'Search & Select', est: '~2 min',
                    lead: "Pick your flight, just like any other booking site.",
                    desc: "We pull real-time prices and availability straight from the airlines, so what you see matches what you'd get booking direct.",
                    youDo: ['Choose origin & destination', 'Pick travel dates', 'Add any passengers'],
                    youGet: 'A locked-in fare, ready for the next step.',
                  },
                  {
                    n: '02', title: 'Pick Your Payment Plan', est: '~1 min',
                    lead: "Two flavors: small deposit, or zero down.",
                    desc: "Defera Installments takes a small deposit that secures your trip price and splits the rest. Defera Zero (refundable fares only) protects your fare with $0 down and first payment later. Either way, once approved, your agreed-upon trip price is locked in.",
                    youDo: ['Choose Installments or Zero', 'Pick weekly or bi-weekly', 'Add a payment method'],
                    youGet: 'Trip approved. Price protected. Schedule generated.',
                  },
                  {
                    n: '03', title: 'Pay Over Time', est: 'Your timeline',
                    lead: "We auto-charge on your schedule. You forget about it.",
                    desc: "Every charge sends an email reminder 48 hours ahead. Pay early any time you want — no fees, no penalties. Skip a payment? We retry, no late charges.",
                    youDo: ['Keep funds available on charge dates', "(Or pay it all off whenever you'd like)"],
                    youGet: 'A progress bar that ticks up to 100%.',
                  },
                  {
                    n: '04', title: 'Get Your Ticket & Fly', est: 'Within 24h',
                    lead: "Final payment clears, your confirmation number follows.",
                    desc: "Once your balance hits $0, your airline confirmation number and e-ticket are released by email and in your dashboard: within 24 hours on weekdays, or by Monday (Tuesday at the latest) when you finish on a Friday afternoon or over the weekend. Every plan finishes before departure, so there's no last-minute scramble.",
                    youDo: ['Show up to the airport'],
                    youGet: "Your PNR and e-ticket. Bon voyage.",
                  },
                ].map((s) => (
                  <Panel key={s.n}>
                    <div className="flex items-start gap-4 sm:gap-5">
                      <div
                        className="flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex-shrink-0 shadow-[inset_0_0_0_1px_rgba(122,138,191,0.30),0_0_30px_-8px_rgba(122,138,191,0.55)]"
                        style={{ background: 'linear-gradient(135deg, rgba(122,138,191,0.22) 0%, rgba(10, 18, 51,0.06) 100%)' }}>
                        <span className="text-[15px] sm:text-base font-medium tabular-nums tracking-tight" style={{ color: '#AAB4D6' }}>{s.n}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 sm:gap-3 mb-2 flex-wrap">
                          <h3 className="text-lg sm:text-xl font-medium text-white tracking-tight">{s.title}</h3>
                          <span className="text-[11px] sm:text-[12px] text-white/40 ml-auto whitespace-nowrap inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-white/[0.08] bg-white/[0.03]">
                            <Clock className="w-3 h-3" />
                            {s.est}
                          </span>
                        </div>
                        <p className="text-[15px] sm:text-base font-medium text-white/90 leading-snug mb-2.5">{s.lead}</p>
                        <p className="text-[14px] sm:text-[15px] text-white/80 leading-relaxed mb-5">{s.desc}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 text-[13px] pt-4 border-t border-white/[0.06]">
                          <div>
                            <div className="mb-2 font-medium uppercase tracking-[0.18em] text-[10px]" style={{ color: '#7A8ABF' }}>You do</div>
                            <ul className="space-y-1.5 text-white/80">
                              {s.youDo.map((x) => (
                                <li key={x} className="flex items-start gap-2">
                                  <CheckCircle className="w-3 h-3 mt-1 flex-shrink-0" style={{ color: '#7A8ABF' }} />
                                  <span>{x}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <div className="mb-2 font-medium uppercase tracking-[0.18em] text-[10px]" style={{ color: '#5eead4' }}>You get</div>
                            <p className="text-white/80">{s.youGet}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Panel>
                ))}
              </div>
            </section>

            {/* 3. Payment plans comparison */}
            <section id="plans" className="scroll-mt-28 mt-16 sm:mt-20 lg:mt-24 pt-12 sm:pt-14 relative before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-white/15 before:to-transparent">
              <div className="flex items-center gap-3 mb-4 sm:mb-5">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full text-[14px] font-medium tabular-nums flex-shrink-0 shadow-[inset_0_0_0_1px_rgba(122,138,191,0.32),0_0_28px_-6px_rgba(122,138,191,0.55)]" style={{ background: 'linear-gradient(135deg, rgba(122,138,191,0.22) 0%, rgba(10, 18, 51,0.08) 100%)', color: '#AAB4D6' }}>3</span>
                <h2 className="text-2xl sm:text-3xl font-light text-white tracking-[-0.015em]">Payment plans</h2>
              </div>
              <p className="text-[16px] sm:text-[17px] text-white/80 leading-relaxed mb-7 sm:mb-9 max-w-2xl">
                <span className="text-white">Two plans.</span> Most people choose Installments. If you want to book without paying anything today, go with Defera Zero.
              </p>

              <Panel innerClassName="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[560px]">
                    <thead>
                      <tr className="border-b border-white/[0.10]">
                        <th className="text-left py-5 px-5 sm:px-6 font-normal text-white/40 text-[10px] uppercase tracking-[0.18em] w-[34%]">Feature</th>
                        <th className="text-left py-5 px-5 sm:px-6 align-bottom"
                          style={{ background: 'linear-gradient(180deg, rgba(10, 18, 51,0.10), rgba(10, 18, 51,0.02))' }}>
                          <div className="flex flex-col items-start gap-2">
                            <span className="text-[10px] sm:text-[11px] font-medium uppercase tracking-[0.18em] inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full"
                              style={{ background: 'rgba(10, 18, 51,0.18)', color: '#7A8ABF', border: '1px solid rgba(10, 18, 51,0.32)' }}>
                              <Star className="w-3 h-3 fill-current" />
                              Most popular
                            </span>
                            <span className="text-white text-lg sm:text-xl font-light tracking-tight">Installments</span>
                          </div>
                        </th>
                        <th className="text-left py-5 px-5 sm:px-6 align-bottom"
                          style={{ background: 'linear-gradient(180deg, rgba(16, 37, 178,0.08), rgba(16, 37, 178,0.015))' }}>
                          <div className="flex flex-col items-start gap-2">
                            <span className="text-[10px] sm:text-[11px] font-medium uppercase tracking-[0.18em] inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full"
                              style={{ background: 'rgba(16, 37, 178,0.15)', color: '#D5DAEB', border: '1px solid rgba(16, 37, 178,0.32)' }}>
                              <Zap className="w-3 h-3" />
                              0% down
                            </span>
                            <span className="text-lg sm:text-xl font-light tracking-tight" style={{ color: '#4E6199' }}>Defera Zero</span>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['Down payment', 'Small deposit at checkout', 'None — $0 due'],
                        ['Eligible fares', 'Any fare type', 'Refundable fares only'],
                        ['Eligible airlines', 'All airlines', 'United, American, Southwest, JetBlue, Virgin Atlantic'],
                        ['Payment cadence', 'Weekly or bi-weekly', 'Weekly or bi-weekly'],
                        ['First payment due', 'At checkout', 'On first scheduled installment date'],
                        ['Price lock', 'At booking', 'At booking'],
                        ['Cancellation', 'Per airline policy', '75% refund up to 24h before departure'],
                        ['E-ticket release', 'On final payment', 'On final payment'],
                        ['Credit check', 'None', 'None'],
                        ['Interest', '0%', '0%'],
                      ].map(([label, a, b], i, arr) => (
                        <tr key={label} className="group/row transition-colors hover:bg-white/[0.02]">
                          <td className={`py-4 px-5 sm:px-6 text-white/55 align-top text-[12px] sm:text-[13px] font-normal uppercase tracking-[0.06em] ${i < arr.length - 1 ? 'border-b border-white/[0.04]' : ''}`}>{label}</td>
                          <td className={`py-4 px-5 sm:px-6 text-white/90 font-normal align-top text-[14px] sm:text-[15px] ${i < arr.length - 1 ? 'border-b border-white/[0.04]' : ''}`}
                            style={{ background: 'rgba(10, 18, 51,0.025)' }}>{a}</td>
                          <td className={`py-4 px-5 sm:px-6 font-normal align-top text-[14px] sm:text-[15px] ${i < arr.length - 1 ? 'border-b border-white/[0.04]' : ''}`}
                            style={{ background: 'rgba(16, 37, 178,0.02)', color: 'rgba(216,228,253,0.92)' }}>{b}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
              <p className="text-[11px] text-white/30 mt-2 sm:hidden">← swipe table to see Defera Zero column</p>
            </section>

            {/* 4. Example payment schedules */}
            <section id="schedules" className="scroll-mt-28 mt-16 sm:mt-20 lg:mt-24 pt-12 sm:pt-14 relative before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-white/15 before:to-transparent">
              <div className="flex items-center gap-3 mb-4 sm:mb-5">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full text-[14px] font-medium tabular-nums flex-shrink-0 shadow-[inset_0_0_0_1px_rgba(122,138,191,0.32),0_0_28px_-6px_rgba(122,138,191,0.55)]" style={{ background: 'linear-gradient(135deg, rgba(122,138,191,0.22) 0%, rgba(10, 18, 51,0.08) 100%)', color: '#AAB4D6' }}>4</span>
                <h2 className="text-2xl sm:text-3xl font-light text-white tracking-[-0.015em]">Example payment schedules</h2>
              </div>
              <p className="text-[16px] sm:text-[17px] text-white/80 leading-relaxed mb-7 sm:mb-9 max-w-2xl">
                <span className="text-white">Three real examples.</span> Each assumes bi-weekly payments with a 15% deposit on a Defera Installments plan. Defera Zero is the same math with $0 down — installments just go up a bit.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
                {[
                  { fare: '$420', deposit: '$63', perPayment: '$71.40', count: 5, weeks: '10 weeks', label: 'Domestic short-haul', cc: '~$15', accent: '127,163,247', accentHex: '#7A8ABF' },
                  { fare: '$890', deposit: '$133.50', perPayment: '$94.55', count: 8, weeks: '16 weeks', label: 'Transcontinental', cc: '~$58', accent: '94,234,212', accentHex: '#5eead4' },
                  { fare: '$1,640', deposit: '$246', perPayment: '$116.40', count: 12, weeks: '24 weeks', label: 'International', cc: '~$140', accent: '10, 18, 51', accentHex: '#7A8ABF' },
                ].map((ex) => (
                  <Panel key={ex.fare} innerClassName="p-0" accent={ex.accent}>
                    {/* Header with themed gradient */}
                    <div className="relative px-6 pt-6 pb-5 border-b border-white/[0.06] overflow-hidden">
                      <div
                        aria-hidden="true"
                        className="pointer-events-none absolute -top-12 right-0 w-[260px] h-[180px] opacity-80"
                        style={{ background: `radial-gradient(ellipse at center, rgba(${ex.accent},0.18), transparent 65%)` }}
                      />
                      <div className="relative flex items-center justify-between gap-2 mb-3">
                        <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full"
                          style={{ background: `rgba(${ex.accent},0.10)`, border: `1px solid rgba(${ex.accent},0.28)` }}>
                          <Plane className="w-3 h-3" style={{ color: ex.accentHex }} />
                          <span className="text-[10px] font-medium uppercase tracking-[0.14em]" style={{ color: ex.accentHex }}>{ex.label}</span>
                        </div>
                      </div>
                      <div className="relative text-4xl sm:text-5xl font-extralight text-white tabular-nums tracking-[-0.025em]">{ex.fare}</div>
                      <div className="relative text-[11px] text-white/40 mt-1.5">Total fare locked in</div>
                    </div>
                    <dl className="px-6 py-5 space-y-3 text-[13px] sm:text-[14px]">
                      <div className="flex justify-between items-baseline gap-3">
                        <dt className="text-white/50">Deposit (15%)</dt>
                        <dd className="text-white/90 font-mono tabular-nums">{ex.deposit}</dd>
                      </div>
                      <div className="flex justify-between items-baseline gap-3">
                        <dt className="text-white/50">Bi-weekly</dt>
                        <dd className="text-white/90 font-mono tabular-nums">{ex.perPayment}</dd>
                      </div>
                      <div className="flex justify-between items-baseline gap-3">
                        <dt className="text-white/50">Installments</dt>
                        <dd className="text-white/90 font-mono tabular-nums">{ex.count}× payments</dd>
                      </div>
                      <div className="flex justify-between items-baseline gap-3">
                        <dt className="text-white/50">Plan length</dt>
                        <dd className="text-white/90">{ex.weeks}</dd>
                      </div>
                      <div className="flex justify-between items-baseline gap-3 pt-3 border-t border-white/[0.06]">
                        <dt className="text-white/65 font-medium">Interest paid</dt>
                        <dd className="font-semibold tabular-nums text-base" style={{ color: ex.accentHex }}>$0.00</dd>
                      </div>
                    </dl>
                    <div className="px-6 py-3.5 border-t border-white/[0.06] text-[12px] sm:text-[13px] flex items-center gap-2"
                      style={{ background: 'rgba(255,255,255,0.015)' }}>
                      <CreditCard className="w-3.5 h-3.5 text-white/30 flex-shrink-0" />
                      <span className="text-white/40">28% APR card:</span>
                      <span className="text-white/75 font-medium tabular-nums ml-auto">{ex.cc} interest</span>
                    </div>
                  </Panel>
                ))}
              </div>
              <div className="mt-4 flex items-start gap-2 text-xs sm:text-[13px] text-white/45">
                <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>These are illustrative. Your actual deposit and cadence are tailored to the fare, departure date, and plan you pick.</span>
              </div>
            </section>

            {/* 5. Eligibility */}
            <section id="eligibility" className="scroll-mt-28 mt-16 sm:mt-20 lg:mt-24 pt-12 sm:pt-14 relative before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-white/15 before:to-transparent">
              <div className="flex items-center gap-3 mb-4 sm:mb-5">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full text-[14px] font-medium tabular-nums flex-shrink-0 shadow-[inset_0_0_0_1px_rgba(122,138,191,0.32),0_0_28px_-6px_rgba(122,138,191,0.55)]" style={{ background: 'linear-gradient(135deg, rgba(122,138,191,0.22) 0%, rgba(10, 18, 51,0.08) 100%)', color: '#AAB4D6' }}>5</span>
                <h2 className="text-2xl sm:text-3xl font-light text-white tracking-[-0.015em]">Eligibility & requirements</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                <Panel accent="94,234,212">
                  <div className="flex items-center gap-2.5 mb-4 pb-4 border-b border-white/[0.06]">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, rgba(94,234,212,0.22), rgba(94,234,212,0.04))', boxShadow: 'inset 0 0 0 1px rgba(94,234,212,0.28)' }}>
                      <CheckCircle className="w-4 h-4" style={{ color: '#5eead4' }} />
                    </div>
                    <h3 className="text-base sm:text-[17px] font-medium text-white">You can sign up if you...</h3>
                  </div>
                  <ul className="text-[14px] sm:text-[15px] text-white/80 space-y-3">
                    {[
                      'Are 18 or older',
                      'Live in the United States',
                      'Have a debit or credit card',
                      'Have an email address',
                      `Are booking ${PLAN_MIN_DAYS}+ days before departure`,
                    ].map((x) => (
                      <li key={x} className="flex items-start gap-2.5">
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#5eead4' }} />
                        <span>{x}</span>
                      </li>
                    ))}
                  </ul>
                </Panel>
                <Panel accent="255,255,255">
                  <div className="flex items-center gap-2.5 mb-4 pb-4 border-b border-white/[0.06]">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 border border-white/[0.10] bg-white/[0.04]">
                      <X className="w-4 h-4 text-white/55" />
                    </div>
                    <h3 className="text-base sm:text-[17px] font-medium text-white">You don't need...</h3>
                  </div>
                  <ul className="text-[14px] sm:text-[15px] text-white/65 space-y-3">
                    {[
                      'A credit check or any credit history',
                      'A minimum income or job',
                      'A bank account (cards work fine)',
                      'A co-signer or guarantor',
                      'A subscription or membership fee',
                    ].map((x) => (
                      <li key={x} className="flex items-start gap-2.5">
                        <X className="w-4 h-4 mt-0.5 flex-shrink-0 text-white/30" />
                        <span className="line-through decoration-white/15 decoration-[1px]">{x}</span>
                      </li>
                    ))}
                  </ul>
                </Panel>
              </div>
            </section>

            {/* 6. Scenarios */}
            <section id="scenarios" className="scroll-mt-28 mt-16 sm:mt-20 lg:mt-24 pt-12 sm:pt-14 relative before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-white/15 before:to-transparent">
              <div className="flex items-center gap-3 mb-4 sm:mb-5">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full text-[14px] font-medium tabular-nums flex-shrink-0 shadow-[inset_0_0_0_1px_rgba(122,138,191,0.32),0_0_28px_-6px_rgba(122,138,191,0.55)]" style={{ background: 'linear-gradient(135deg, rgba(122,138,191,0.22) 0%, rgba(10, 18, 51,0.08) 100%)', color: '#AAB4D6' }}>6</span>
                <h2 className="text-2xl sm:text-3xl font-light text-white tracking-[-0.015em]">What happens if...</h2>
              </div>
              <p className="text-[16px] sm:text-[17px] text-white/80 leading-relaxed mb-7 sm:mb-9 max-w-2xl">
                <span className="text-white">The stuff people actually worry about</span> — answered straight, no fine print.
              </p>

              <div className="space-y-3">
                {[
                  {
                    q: 'What if I miss a payment?',
                    short: "Nothing scary. No late fees, ever.",
                    a: "We retry the charge up to 3 times over 5 days, with email and SMS reminders so you can fix it. If retries fail, your booking pauses (it doesn't cancel), and you can catch up right from your trip. You're never charged a late fee.",
                    icon: AlertCircle,
                  },
                  {
                    q: 'What if I need to cancel?',
                    short: "Defera Zero: 75% back, up to 24h before departure.",
                    a: "On Defera Zero, cancel any time up to 24 hours before departure and get 75% of payments back, or take 100% as Defera Credit toward a future trip. On Defera Installments, your trip's Cancellation Policy screen shows your options before you confirm; on eligible bookings, what you've paid comes back as Defera Credit.",
                    icon: X,
                  },
                  {
                    q: 'What if the fare changes after I book?',
                    short: "You're protected from price hikes.",
                    a: "Across both plans, your agreed-upon trip price is secured once your booking is approved. If prices spike, you're protected — you keep paying what you agreed to while you remain in good standing.",
                    icon: DollarSign,
                  },
                  {
                    q: 'How does seat selection work?',
                    short: "Our concierge picks the best seats for you, free.",
                    a: "If your fare comes with complimentary seat selection, our concierge manually chooses the best available seats — by default a window seat as close to the front as possible, and seated together if you're traveling with companions. Want to pick specific seats yourself? On supported airlines you can choose them on a live seat map at checkout, or after booking from Flight Options → Purchase Seats.",
                    icon: Armchair,
                  },
                  {
                    q: 'What if the airline cancels my flight?',
                    short: "Tell us right away and we'll help you through it.",
                    a: "If the airline changes or cancels your flight, you did nothing wrong. It's handled under the operating airline's rules, and we'll help you with what comes next: contact us as soon as you hear from the airline.",
                    icon: Plane,
                  },
                  {
                    q: 'Can I pay it all off early?',
                    short: "Yes. Pay it off whenever — no fees.",
                    a: "Pay any remaining balance whenever you want, with no early-payoff fees. Once your trip is paid in full, your airline confirmation number is released, within 24 hours on weekdays.",
                    icon: Zap,
                  },
                  {
                    q: "What if I'm flying before my plan ends?",
                    short: "Won't happen — every plan finishes before takeoff.",
                    a: "We size every plan to finish before your trip. Once your final payment clears, your airline confirmation number is released: within 24 hours for a weekday payment, or by Monday (Tuesday at the latest) for one made on a Friday afternoon or over the weekend.",
                    icon: Calendar,
                  },
                ].map(({ q, short, a, icon: Icon }) => (
                  <Panel key={q} accent="94,234,212">
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, rgba(94,234,212,0.18), rgba(94,234,212,0.03))', boxShadow: 'inset 0 0 0 1px rgba(94,234,212,0.25), 0 0 24px -8px rgba(94,234,212,0.4)' }}>
                        <Icon className="w-4 h-4" style={{ color: '#5eead4' }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-[15px] sm:text-base font-medium text-white mb-2.5 tracking-tight">{q}</h3>
                        <div className="flex items-start gap-2 mb-3 px-3 py-2 rounded-lg"
                          style={{ background: 'rgba(94,234,212,0.06)', border: '1px solid rgba(94,234,212,0.16)' }}>
                          <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: '#5eead4' }} />
                          <p className="text-[13px] sm:text-[14px] font-medium leading-snug" style={{ color: '#a7f3d0' }}>{short}</p>
                        </div>
                        <p className="text-[14px] sm:text-[15px] text-white/80 leading-relaxed">{a}</p>
                      </div>
                    </div>
                  </Panel>
                ))}
              </div>
            </section>

            {/* 7. Miles & rewards */}
            <section id="rewards" className="scroll-mt-28 mt-16 sm:mt-20 lg:mt-24 pt-12 sm:pt-14 relative before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-white/15 before:to-transparent">
              <div className="flex items-center gap-3 mb-4 sm:mb-5">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full text-[14px] font-medium tabular-nums flex-shrink-0 shadow-[inset_0_0_0_1px_rgba(122,138,191,0.32),0_0_28px_-6px_rgba(122,138,191,0.55)]" style={{ background: 'linear-gradient(135deg, rgba(122,138,191,0.22) 0%, rgba(10, 18, 51,0.08) 100%)', color: '#AAB4D6' }}>7</span>
                <h2 className="text-2xl sm:text-3xl font-light text-white tracking-[-0.015em]">Miles & rewards</h2>
              </div>
              <p className="text-[16px] sm:text-[17px] text-white/80 leading-relaxed mb-7 sm:mb-9 max-w-2xl">
                <span className="text-white">You earn rewards three different ways</span> on every booking — at the same time. Most travelers don't realize this is allowed.
              </p>

              <Panel innerClassName="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[480px]">
                    <thead>
                      <tr className="border-b border-white/[0.08]" style={{ background: 'rgba(255,255,255,0.025)' }}>
                        <th className="text-left py-3.5 px-4 sm:px-5 font-normal text-white/45 text-[11px] uppercase tracking-wider">Source</th>
                        <th className="text-left py-3.5 px-4 sm:px-5 font-normal text-white/45 text-[11px] uppercase tracking-wider">What you earn</th>
                        <th className="text-left py-3.5 px-4 sm:px-5 font-normal text-white/45 text-[11px] uppercase tracking-wider">Credited by</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['Credit card payments', "Your card's normal travel/cashback rewards", 'Card issuer'],
                        ['Airline frequent flyer', 'Standard mileage based on fare class', 'Airline'],
                        ['Defera Miles', '2 Defera Miles per $1 spent', 'Defera, post-booking'],
                      ].map(([s, w, c], i, arr) => (
                        <tr key={s} className={i % 2 === 1 ? 'bg-white/[0.02]' : ''}>
                          <td className={`py-3.5 px-4 sm:px-5 text-white/85 text-[13px] sm:text-[14px] font-medium ${i < arr.length - 1 ? 'border-b border-white/[0.04]' : ''}`}>{s}</td>
                          <td className={`py-3.5 px-4 sm:px-5 text-white/80 text-[13px] sm:text-[14px] ${i < arr.length - 1 ? 'border-b border-white/[0.04]' : ''}`}>{w}</td>
                          <td className={`py-3.5 px-4 sm:px-5 text-white/60 text-[13px] sm:text-[14px] ${i < arr.length - 1 ? 'border-b border-white/[0.04]' : ''}`}>{c}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>

              {/* Worked example callout */}
              <div className="mt-4 rounded-2xl border p-4 sm:p-5"
                style={{ borderColor: 'rgba(16, 37, 178,0.22)', background: 'linear-gradient(135deg, rgba(16, 37, 178,0.06), rgba(16, 37, 178,0.04))' }}>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(16, 37, 178,0.15)', border: '1px solid rgba(16, 37, 178,0.3)' }}>
                    <Star className="w-4 h-4" style={{ color: '#7A8ABF' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] uppercase tracking-wider text-white/40 font-normal mb-1">Quick math</p>
                    <p className="text-[14px] sm:text-[15px] text-white/80 leading-relaxed">
                      An <span className="text-white font-medium">$890 booking</span> = your card's points + the airline's miles + <span className="text-white font-medium">1,780 Defera Miles</span> (worth $22.25 off your next Defera trip). You'd never get all three booking direct.
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-xs sm:text-[13px] text-white/45 mt-3">Defera Miles are worth 1.25¢ each off a future Defera trip, taken off the price at checkout, or 1¢ each as e-gift cards.</p>
            </section>

            {/* 8. Extras: Credits + JSX */}
            <section id="extras" className="scroll-mt-28 mt-16 sm:mt-20 lg:mt-24 pt-12 sm:pt-14 relative before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-white/15 before:to-transparent">
              <div className="flex items-center gap-3 mb-4 sm:mb-5">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full text-[14px] font-medium tabular-nums flex-shrink-0 shadow-[inset_0_0_0_1px_rgba(122,138,191,0.32),0_0_28px_-6px_rgba(122,138,191,0.55)]" style={{ background: 'linear-gradient(135deg, rgba(122,138,191,0.22) 0%, rgba(10, 18, 51,0.08) 100%)', color: '#AAB4D6' }}>8</span>
                <h2 className="text-2xl sm:text-3xl font-light text-white tracking-[-0.015em]">Credits, gift cards & semi-private</h2>
              </div>

              <p className="text-[16px] sm:text-[17px] text-white/80 leading-relaxed mb-7 sm:mb-9 max-w-2xl">
                <span className="text-white">Already have an airline credit gathering dust?</span> Or curious about premium semi-private travel? Both work with Defera.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <Panel>
                  <div className="flex items-center gap-2 mb-3">
                    <Gift className="w-4 h-4 flex-shrink-0" style={{ color: '#7A8ABF' }} />
                    <h3 className="text-[15px] font-medium text-white">Use the credits you already have</h3>
                  </div>
                  <p className="text-[14px] sm:text-[15px] text-white/80 leading-relaxed mb-4">
                    Got an airline voucher, an unused travel credit, or a gift card sitting around? Apply it at checkout. We split the rest into installments — no need to use the whole credit at once.
                  </p>
                  <ul className="text-[13px] sm:text-[14px] text-white/70 space-y-2">
                    {['Partial credit usage is fine', 'Stack multiple gift cards per booking', 'Validated automatically at checkout'].map((x) => (
                      <li key={x} className="flex gap-2"><span className="text-white/30 flex-shrink-0">·</span><span>{x}</span></li>
                    ))}
                  </ul>
                </Panel>
                {/* Indigo-tinted JSX panel — same sturdy structure with accent overlay */}
                <div className="relative overflow-hidden rounded-2xl border backdrop-blur-sm shadow-[0_0_0_1px_rgba(255,255,255,0.03)_inset]"
                  style={{ borderColor: 'rgba(16, 37, 178,0.22)', background: 'linear-gradient(135deg, rgba(16, 37, 178,0.08), rgba(10, 18, 51,0.04))' }}>
                  <div className="absolute inset-x-0 top-0 h-px pointer-events-none" style={{ background: 'linear-gradient(90deg, transparent, rgba(177,200,252,0.4), transparent)' }} />
                  <div className="relative p-5 sm:p-6 md:p-7">
                    <div className="flex items-center gap-2 mb-3">
                      <Zap className="w-4 h-4 flex-shrink-0" style={{ color: '#AAB4D6' }} />
                      <h3 className="text-[15px] font-medium text-white">Semi-private with JSX</h3>
                    </div>
                    <p className="text-[14px] sm:text-[15px] text-white/80 leading-relaxed mb-4">
                      Want a more luxe travel day? Book JSX semi-private flights on installments — private terminal, 20-minute check-in, no boarding chaos. Same plans, same fare lock.
                    </p>
                    <ul className="text-[13px] sm:text-[14px] text-white/70 space-y-2">
                      {['Works with Installments and Defera Zero', 'Same price-lock guarantee', 'Still earns you Defera Miles'].map((x) => (
                        <li key={x} className="flex gap-2"><span className="text-white/30 flex-shrink-0">·</span><span>{x}</span></li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* 9. FAQ */}
            <section id="faq" className="scroll-mt-28 mt-16 sm:mt-20 lg:mt-24 pt-12 sm:pt-14 relative before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-white/15 before:to-transparent">
              <div className="flex items-center gap-3 mb-4 sm:mb-5">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full text-[14px] font-medium tabular-nums flex-shrink-0 shadow-[inset_0_0_0_1px_rgba(122,138,191,0.32),0_0_28px_-6px_rgba(122,138,191,0.55)]" style={{ background: 'linear-gradient(135deg, rgba(122,138,191,0.22) 0%, rgba(10, 18, 51,0.08) 100%)', color: '#AAB4D6' }}>9</span>
                <h2 className="text-2xl sm:text-3xl font-light text-white tracking-[-0.015em]">Frequently asked questions</h2>
              </div>

              <p className="text-[16px] sm:text-[17px] text-white/80 leading-relaxed mb-7 max-w-2xl">
                <span className="text-white">Twelve real questions</span> we get from new travelers. Tap to expand.
              </p>

              <Panel innerClassName="p-0">
                <div className="divide-y divide-white/[0.06]">
                  {HOW_IT_WORKS_FAQS.map(({ q, a }) => (
                    <details key={q} className="group">
                      <summary className="cursor-pointer list-none flex items-start justify-between gap-3 sm:gap-4 px-4 sm:px-5 py-4 hover:bg-white/[0.02] transition-colors">
                        <span className="text-[14px] sm:text-[15px] font-medium text-white pr-2">{q}</span>
                        <ChevronRight className="w-4 h-4 text-white/30 transition-transform flex-shrink-0 mt-0.5 group-open:rotate-90" />
                      </summary>
                      <div className="px-4 sm:px-5 pb-4 text-[14px] sm:text-[15px] text-white/80 leading-relaxed">{a}</div>
                    </details>
                  ))}
                </div>
              </Panel>

              {/* Still have questions callout */}
              <div className="mt-4 sm:mt-5 rounded-2xl border border-white/[0.08] p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                style={{ background: 'rgba(255,255,255,0.025)' }}>
                <div className="flex items-start gap-3">
                  <Mail className="w-4 h-4 text-white/40 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-[14px] sm:text-[15px] text-white font-medium">Still have a question?</p>
                    <p className="text-[13px] sm:text-[14px] text-white/70 mt-0.5"><Link to="/contact#message" className="text-white/80 underline hover:text-white">Send us a message</Link> and our team will reply within 24 hours.</p>
                  </div>
                </div>
                <button onClick={() => openOverlay('contact')}
                  className="text-xs sm:text-sm font-medium px-4 py-2.5 rounded-full whitespace-nowrap self-start sm:self-auto"
                  style={{ background: 'rgba(10, 18, 51,0.14)', color: '#7A8ABF', border: '1px solid rgba(10, 18, 51,0.3)' }}>
                  Contact us
                </button>
              </div>
            </section>

            {/* Bottom action strip */}
            <section className="mt-16 sm:mt-20 lg:mt-24 pt-12 sm:pt-14 relative before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-white/15 before:to-transparent">
              <Panel>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6">
                  <div>
                    <h3 className="text-lg sm:text-xl font-medium text-white mb-1.5">Try a real search.</h3>
                    <p className="text-[14px] sm:text-[15px] text-white/80 leading-relaxed">No commitment — see what your actual flight + payment schedule would look like in 30 seconds.</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 sm:flex-shrink-0">
                    <Button onClick={() => navigate('/home')} variant="outline"
                      className="px-5 py-4 text-sm font-normal rounded-full border-white/15 bg-transparent text-white hover:bg-white/5">
                      Sign up
                    </Button>
                    <Button onClick={() => navigate('/')}
                      className="px-6 py-4 text-sm font-medium rounded-full text-white border-0"
                      style={{ background: 'linear-gradient(135deg, #2A3868, #0A1233)', boxShadow: '0 8px 24px -8px rgba(10, 18, 51,0.5)' }}>
                      Search flights →
                    </Button>
                  </div>
                </div>
              </Panel>
            </section>

          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 sm:py-10 px-5 sm:px-6 border-t border-white/[0.06] bg-black">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-2 mb-6">
            {[
              { label: 'Terms & Conditions', key: 'terms' },
              { label: 'Customer Stories', key: 'customers' },
              { label: 'Privacy Policy', key: 'privacy' },
              { label: 'Press', key: 'press' },
              { label: 'Help Center', key: 'help' },
              { label: 'Security', key: 'security' },
              { label: 'Do Not Sell or Share My Personal Information', key: 'privacy-choices' },
              { label: 'Newsroom', key: 'newsroom' },
              { label: 'Contact Us', key: 'contact' },
              { label: 'Our Charter', key: 'charter' },
              { label: 'About Us', key: 'about' },
            ].map(({ label, key }) => (
              <button key={key} onClick={() => openOverlay(key)}
                className="text-white/35 hover:text-white/65 transition-colors text-xs font-normal text-left">
                {label}
              </button>
            ))}
          </div>
          <p className="text-white/25 text-xs">© 2026 Defera LLC. All rights reserved.</p>
        </div>
      </footer>

      {/* Overlay Modal */}
      <AnimatePresence>
        {overlayContent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm overflow-hidden"
            onClick={closeOverlay}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-black border border-white/10 rounded-2xl max-w-4xl w-full h-[90vh] flex flex-col relative"
            >
              <div className="flex-shrink-0 p-6 border-b border-white/10 flex items-center justify-between">
                <h2 className="text-xl font-light text-white">
                  {overlayContent === 'terms' && 'Terms of Service'}
                  {overlayContent === 'customers' && 'Customer Stories'}
                  {overlayContent === 'privacy' && 'Privacy Policy'}
                  {overlayContent === 'press' && 'Press & Media'}
                  {overlayContent === 'newsroom' && 'Newsroom'}
                  {overlayContent === 'contact' && 'Contact Us'}
                  {overlayContent === 'charter' && 'Our Charter'}
                  {overlayContent === 'about' && 'About Defera'}
                </h2>
                <button
                  onClick={closeOverlay}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className={`flex-1 min-h-0 ${overlayContent === 'terms' || overlayContent === 'privacy' ? 'overflow-hidden' : 'overflow-y-auto p-8'}`}>
                {overlayContent === 'terms' && (
                  <iframe
                    title="Terms of Service"
                    src="/terms?embed=1"
                    className="w-full h-full border-0"
                  />
                )}
                {overlayContent === 'privacy' && (
                  <iframe
                    title="Privacy Policy"
                    src="/privacy?embed=1"
                    className="w-full h-full border-0"
                  />
                )}
                {overlayContent === 'contact' && (
                  <div className="text-white/80">
                    <h1 className="text-3xl font-light text-white mb-2">Contact Us</h1>
                    <p className="text-[12.5px] text-white/80 mb-12 leading-relaxed max-w-3xl">
                      Get in touch with our concierge team for any inquiries or support.
                    </p>
                    <div className="space-y-6 text-sm">
                      {/* Was a bare sms: link to the concierge (with no
                          pre-filled body — this panel had already drifted
                          from the real Contact page it duplicates). Routes
                          to Tommy now, same as everywhere else. */}
                      <div className="flex items-start gap-4">
                        <MessageSquareText className="w-5 h-5 text-white/60 mt-0.5 flex-shrink-0" />
                        <div>
                          <h2 className="text-white mb-1 font-normal">Chat with Defera Support</h2>
                          <Link
                            to={buildTommyUrl()}
                            className="text-white/60 hover:text-white transition-colors text-xs font-normal underline"
                          >
                            Start a conversation
                          </Link>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <Mail className="w-5 h-5 text-white/60 mt-0.5 flex-shrink-0" />
                        <div>
                          <h2 className="text-white mb-1 font-normal">Send a Message</h2>
                          <Link
                            to="/contact#message"
                            className="text-white/60 hover:text-white transition-colors text-xs font-normal underline"
                          >
                            Write to our team
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {overlayContent === 'about' && (
                  <div className="text-white/80">
                    <h1 className="text-4xl md:text-5xl font-light text-white mb-4 tracking-tight">
                      About Defera
                    </h1>
                    <p className="text-lg text-white/80 leading-relaxed mb-6">
                      We're building the travel platform that should have existed all along—one that treats access to air travel as a right, not a privilege reserved for those with perfect credit and large bank accounts.
                    </p>
                    <div className="space-y-6 text-sm leading-relaxed">
                      <section>
                        <h2 className="text-base font-normal text-white mb-2">The Problem We Saw</h2>
                        <p className="text-white/80">
                          Air travel has become increasingly expensive and inaccessible. Average domestic round-trip fares in the U.S. exceed $350—a prohibitive barrier for the 40% of Americans who can't cover a $400 emergency expense. Credit-based travel rewards programs, 'buy now pay later' options with hidden interest, and volatile pricing create a system that advantages the wealthy and excludes everyone else. Millions of people postpone or abandon travel plans not because they lack desire, but because they lack access to fair financing.
                        </p>
                      </section>
                      <section>
                        <h2 className="text-base font-normal text-white mb-2">Our Approach</h2>
                        <p className="text-white/80">
                          We asked a simple question: what if travel booking worked like layaway? Pay a deposit, make installment payments, receive your ticket when you've paid in full. No credit checks. No interest charges. No surprise fees. Just transparent terms and predictable costs. We built partnerships with major airlines to access real-time inventory and competitive pricing. We integrated with payment infrastructure that automates installments without friction. We designed interfaces optimized for mobile devices and slower internet connections because that's how our customers access the world.
                        </p>
                      </section>
                      <section>
                        <h2 className="text-base font-normal text-white mb-2">The Results</h2>
                        <p className="text-white/80">
                          In less than a year, we've served over 50,000 travelers across 120+ countries. Our customer satisfaction rating stands at 4.9/5. We've facilitated family reunions, enabled students to study abroad, helped workers relocate for better opportunities, and made dream vacations possible for people who'd given up on them. Our 340% quarter-over-quarter growth reflects genuine demand—word spreads quickly when a service actually solves problems instead of creating new ones.
                        </p>
                      </section>
                      <section>
                        <h2 className="text-base font-normal text-white mb-2">What Makes Us Different</h2>
                        <p className="text-white/80">
                          We're not a travel agency adding markup. We're not a lender charging hidden interest. We're a platform that rethinks how travel booking should work in an era where traditional finance excludes too many people. Our business model is straightforward: transparent service fees disclosed upfront, no revenue from data sales, no predatory lending practices. We earn money when you successfully book travel, which aligns our incentives with yours. If you don't travel, we don't profit. That focus keeps us honest.
                        </p>
                      </section>
                      <section>
                        <h2 className="text-base font-normal text-white mb-2">Where We're Going</h2>
                        <p className="text-white/80">
                          This is just the beginning. We're expanding partnerships with international carriers to offer more routes and better pricing. We're building AI-powered tools that suggest optimal booking times to maximize savings. We're developing group booking features for families and teams traveling together. We're exploring hotel and car rental partnerships with the same installment-payment model. Every expansion is guided by one principle: does this make travel more accessible for more people? If the answer is yes, we build it.
                        </p>
                      </section>
                      <section className="border-t border-white/10 pt-6 mt-6">
                        <h3 className="text-base font-normal text-white mb-3">Get in Touch</h3>
                        <p className="text-white/80 mb-4">
                          Questions about our platform? Interested in partnerships? Want to share your travel story? We'd love to hear from you.
                        </p>
                        <div className="flex flex-col gap-2 text-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-white/50">General inquiries:</span>
                            <a href="mailto:hello@defera.app" className="text-[var(--accent-color)] hover:text-[var(--accent-color-hover)] transition-colors">
                              hello@defera.app
                            </a>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-white/50">Customer support:</span>
                            <Link to="/contact#message" className="text-[var(--accent-color)] hover:text-[var(--accent-color-hover)] transition-colors">
                              Send us a message
                            </Link>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-white/50">Press & media:</span>
                            <a href="mailto:press@defera.app" className="text-[var(--accent-color)] hover:text-[var(--accent-color-hover)] transition-colors">
                              press@defera.app
                            </a>
                          </div>
                        </div>
                      </section>
                    </div>
                  </div>
                )}
                {overlayContent === 'customers' && (
                  <div className="text-white/80">
                    <h1 className="text-3xl font-light text-white mb-4">Customer Stories</h1>
                    <p className="text-base text-white/80 mb-8 leading-relaxed">
                      Real people, real journeys. See how everyday travelers are using Defera to visit family, chase opportunities, and make memories without breaking the bank.
                    </p>
                    <div className="space-y-8 text-sm">
                      {[
                        {
                          name: "Maria Rodriguez",
                          occupation: "Retail Associate",
                          location: "Phoenix, AZ",
                          quote: "I've been saving to visit my family in Mexico for two years. With Defera's payment plan, I finally booked my ticket and paid it off over six months. No more waiting years to see my mom.",
                          achievement: "First international trip in 5 years"
                        },
                        {
                          name: "James Carter",
                          occupation: "Warehouse Worker",
                          location: "Columbus, OH",
                          quote: "My daughter's wedding was in California and I'm in Ohio. I thought I'd have to miss it because I couldn't afford the upfront cost. Defera let me split the payments and I made it to walk her down the aisle.",
                          achievement: "Attended daughter's wedding"
                        },
                        {
                          name: "Priya Patel",
                          occupation: "Restaurant Server",
                          location: "Houston, TX",
                          quote: "I work two jobs and tips are unpredictable. When my brother got sick back home in India, I needed to fly ASAP but didn't have $1,200 sitting around. Defera's instant approval with no credit check was a lifesaver.",
                          achievement: "Emergency family visit within 24 hours"
                        },
                        {
                          name: "Marcus Johnson",
                          occupation: "Delivery Driver",
                          location: "Atlanta, GA",
                          quote: "I've been driving for DoorDash for three years, saving every penny to take my kids to Disney. Other booking sites wanted everything upfront. Defera let me pay $150 a month and my kids got their dream vacation.",
                          achievement: "First family vacation in 8 years"
                        },
                        {
                          name: "Lisa Nguyen",
                          occupation: "Home Health Aide",
                          location: "Seattle, WA",
                          quote: "I take care of elderly patients 60 hours a week. When my best friend invited me to her destination wedding in Hawaii, I thought there was no way. Defera made it possible - I'm going to my first beach vacation ever at 42.",
                          achievement: "First vacation in adult life"
                        },
                        {
                          name: "David Gonzales",
                          occupation: "Construction Worker",
                          location: "Denver, CO",
                          quote: "Construction work is seasonal. I needed to fly to Texas for a job opportunity but it was during my slow season. Defera's flexible payments meant I could book now and pay when work picked back up. Got the job!",
                          achievement: "Secured new job opportunity"
                        }
                      ].map((story, index) => (
                        <div key={index} className="bg-white/5 border border-white/10 rounded-xl p-6">
                          <h3 className="text-lg font-light text-white mb-1">{story.name}</h3>
                          <p className="text-xs text-white/60 mb-3">{story.occupation} · {story.location}</p>
                          <div className="bg-[var(--accent-color)]/20 text-[var(--accent-color)] text-xs px-3 py-1 rounded-full inline-block mb-4">
                            {story.achievement}
                          </div>
                          <p className="text-sm text-white/80 leading-relaxed italic">"{story.quote}"</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {overlayContent === 'press' && (
                  <div className="text-white/80">
                    <h1 className="text-3xl font-light text-white mb-4">Press & Media</h1>
                    <p className="text-base text-white/80 mb-8 leading-relaxed">
                      Resources and story ideas for journalists covering innovation in travel, fintech, and consumer technology.
                    </p>
                    <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-8">
                      <div className="flex items-center gap-3 text-sm">
                        <Mail className="w-5 h-5 text-[var(--accent-color)]" />
                        <div>
                          <div className="text-white/50 text-xs mb-1">Media Inquiries</div>
                          <a href="mailto:press@defera.app" className="text-white hover:text-[var(--accent-color)] transition-colors">
                            press@defera.app
                          </a>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-6 text-sm">
                      <section>
                        <h2 className="text-lg font-light text-white mb-4">Company Overview</h2>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <div className="text-white/50 text-xs mb-1">Founded</div>
                            <div className="text-white">2024</div>
                          </div>
                          <div>
                            <div className="text-white/50 text-xs mb-1">Location</div>
                            <div className="text-white">San Francisco, CA</div>
                          </div>
                        </div>
                      </section>
                      <section>
                        <h2 className="text-lg font-light text-white mb-4">Story Ideas</h2>
                        <div className="space-y-4">
                          {[
                            {
                              category: "Financial Innovation",
                              title: "The rise of layaway booking in the travel industry",
                              description: "How Defera is democratizing air travel through flexible payment solutions and no credit check booking."
                            },
                            {
                              category: "Technology & Innovation",
                              title: "AI-Powered Travel Planning",
                              description: "Defera leverages advanced technology to streamline flight booking, price protection, and trip management."
                            },
                            {
                              category: "Consumer Trends",
                              title: "The Shifting Travel Landscape",
                              description: "Post-pandemic travel behavior shows unprecedented demand for flexibility and affordability."
                            }
                          ].map((story, index) => (
                            <div key={index} className="bg-white/5 border border-white/10 rounded-xl p-6">
                              <div className="bg-[var(--accent-color)]/20 text-[var(--accent-color)] text-xs px-3 py-1 rounded-full inline-block mb-3">
                                {story.category}
                              </div>
                              <h3 className="text-base font-normal text-white mb-2">{story.title}</h3>
                              <p className="text-sm text-white/80 leading-relaxed">{story.description}</p>
                            </div>
                          ))}
                        </div>
                      </section>
                    </div>
                  </div>
                )}
                {overlayContent === 'newsroom' && (
                  <div className="text-white/80">
                    <h1 className="text-3xl font-light text-white mb-4">Newsroom</h1>
                    <p className="text-base text-white/80 mb-8 leading-relaxed">
                      The latest news, updates, and milestones from Defera. Follow our journey to make air travel accessible for everyone.
                    </p>
                    <div className="space-y-6 text-sm">
                      {[
                        {
                          date: "January 15, 2025",
                          title: "Defera Surpasses 50,000 Active Travelers",
                          category: "Milestone",
                          content: "Defera today announced that its platform has officially served over 50,000 active travelers, marking a significant milestone in the company's mission to democratize air travel."
                        },
                        {
                          date: "December 8, 2024",
                          title: "Defera Introduces Price Protection Guarantee",
                          category: "Product",
                          content: "Defera has launched a comprehensive price protection feature that shields travelers from fare increases after booking. The new capability addresses one of the most common pain points in travel planning."
                        },
                        {
                          date: "November 22, 2024",
                          title: "340% Year-Over-Year Growth Reflects Surge in Installment-Based Travel",
                          category: "Growth",
                          content: "Defera reported 340% growth in its user base compared to the previous quarter, signaling a fundamental shift in how consumers approach travel purchasing."
                        }
                      ].map((release, index) => (
                        <article key={index} className="border-b border-white/10 pb-6 last:border-0">
                          <div className="mb-3">
                            <span className="inline-block px-3 py-1 bg-[var(--accent-color)]/10 text-[var(--accent-color)] text-xs rounded-full mb-2">
                              {release.category}
                            </span>
                            <p className="text-xs text-white/40">{release.date}</p>
                          </div>
                          <h3 className="text-base font-normal text-white mb-3 leading-tight">{release.title}</h3>
                          <p className="text-sm text-white/80 leading-relaxed">{release.content}</p>
                        </article>
                      ))}
                    </div>
                    <div className="mt-8 bg-white/5 border border-white/10 rounded-xl p-6">
                      <h3 className="text-base font-normal text-white mb-3">Media Inquiries</h3>
                      <p className="text-sm text-white/60 mb-4 leading-relaxed">
                        For press inquiries, interview requests, or additional information, please contact our media team.
                      </p>
                      <a href="mailto:press@defera.app" className="text-sm text-[var(--accent-color)] hover:text-[var(--accent-color-hover)] transition-colors">
                        press@defera.app
                      </a>
                    </div>
                  </div>
                )}
                {overlayContent === 'charter' && (
                  <div className="text-white/80">
                    <h1 className="text-3xl font-light text-white mb-4">Our Charter</h1>
                    <p className="text-base text-white/80 mb-8 leading-relaxed">
                      The principles and commitments that guide everything we build at Defera. This is who we are, what we believe, and what you can hold us accountable to.
                    </p>
                    <div className="space-y-6 text-sm">
                      <section className="bg-white/5 border border-white/10 rounded-xl p-6">
                        <h2 className="text-lg font-light text-white mb-3">Our Mission</h2>
                        <p className="text-sm text-white/80 leading-relaxed mb-3">
                          Make air travel accessible and affordable for everyone through transparent pricing, flexible payments, and radical inclusion.
                        </p>
                        <p className="text-[12.5px] text-white/80 leading-relaxed">
                          We exist because millions of people want to travel but face financial barriers. Credit checks exclude them. Upfront costs strand them. Hidden fees surprise them. Traditional travel booking wasn't built for working-class families, students, gig workers, or anyone living paycheck to paycheck. We're changing that.
                        </p>
                      </section>
                      <section>
                        <h2 className="text-lg font-light text-white mb-4">Core Principles</h2>
                        <div className="space-y-4">
                          {[
                            {
                              title: "Accessibility First",
                              description: "We believe travel is a fundamental human desire, not a luxury reserved for the wealthy. Every feature, every policy, and every decision we make starts with one question: does this make travel more accessible?"
                            },
                            {
                              title: "Customer-Centric Always",
                              description: "We don't have 'users' or 'accounts'—we have people with dreams, obligations, and constraints. Every support inquiry is answered by humans who actually care."
                            },
                            {
                              title: "Global Inclusion",
                              description: "Travel connects cultures, families, and opportunities. But traditional travel booking excludes billions through credit score requirements, currency barriers, and Western-centric design."
                            }
                          ].map((principle, index) => (
                            <div key={index} className="border-l-2 border-[var(--accent-color)]/30 pl-4">
                              <h3 className="text-base font-normal text-white mb-2">{principle.title}</h3>
                              <p className="text-[12.5px] text-white/80 leading-relaxed">{principle.description}</p>
                            </div>
                          ))}
                        </div>
                      </section>
                      <section>
                        <h2 className="text-lg font-light text-white mb-4">Our Commitments</h2>
                        <div className="space-y-4">
                          {[
                            {
                              title: "We will never sell your data",
                              description: "Your travel plans, payment information, and personal details are yours. We don't sell data to advertisers, we don't share it with data brokers, and we don't monetize your privacy."
                            },
                            {
                              title: "We will honor our price locks",
                              description: "When we lock in your price, that's a promise. Market volatility, fuel surcharges, carrier fare increases—none of that gets passed to you."
                            },
                            {
                              title: "We will never require credit checks",
                              description: "Your credit score says nothing about your desire to visit loved ones or chase opportunities. We will never implement credit checks, hard inquiries, or scoring systems that exclude people."
                            }
                          ].map((commitment, index) => (
                            <div key={index} className="bg-white/5 border border-white/10 rounded-lg p-4">
                              <h3 className="text-sm font-normal text-white mb-2">{commitment.title}</h3>
                              <p className="text-[12.5px] text-white/80 leading-relaxed">{commitment.description}</p>
                            </div>
                          ))}
                        </div>
                      </section>
                      <section className="bg-white/5 border border-white/10 rounded-xl p-6 mt-8">
                        <h3 className="text-base font-normal text-white mb-3">Hold Us Accountable</h3>
                        <p className="text-sm text-white/80 leading-relaxed">
                          This charter means nothing if we don't live it. If you experience something that contradicts these principles, we want to know. Email us at <span className="text-[var(--accent-color)]">charter@defera.app</span> with concerns, feedback, or suggestions.
                        </p>
                      </section>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </>
  );
}

